import { Collection } from 'core/model';
import { action, computed } from 'mobx';
import { omit } from 'lodash';
import $auth from 'app/stores/$auth';
import { isKProxyAgentUser } from '../util/automatedUsers';
import ChatSessionModel from './ChatSessionModel';

const CLIENT_STATE_FIELDS = ['messages', 'isThinking', 'isAgentHeartbeatAlive', 'enabledToolConfigs'];

class ChatSessionCollection extends Collection {
  get model() {
    return ChatSessionModel;
  }

  get url() {
    return '/api/ui/kaizen/listChatSessions';
  }

  // kproxy agent chats are sudo users testing a company's AI Advisor; keep them out of the company's own lists
  deserialize(data) {
    const sessions = super.deserialize(data) || [];
    return $auth.isSudoer ? sessions : sessions.filter((session) => !isKProxyAgentUser(session.user));
  }

  /**
   * The AI Advisor renders before the chat list has loaded, so a chat can be opened or started while the
   * fetch is in flight. Keep that chat's model instance (messages, socket subscription) instead of letting
   * the fetched list replace it.
   */
  processData(response, fetchOptions = {}) {
    const { selected } = this;
    const previousModels = this.original || [];

    return super.processData(response, fetchOptions).then((result) => {
      if (selected && !Array.isArray(selected)) {
        this.retainModel(selected);
      }
      this.unsubscribeReplacedModels(previousModels);
      return result;
    });
  }

  /**
   * Replaced instances can still hold a session socket. Unsubscribing removes every listener on that
   * session's channel, so do it now (before a fresh instance subscribes) rather than leaving an orphan
   * that could later tear down the live instance's listener.
   */
  unsubscribeReplacedModels(previousModels) {
    previousModels.forEach((model) => {
      if (model.socket && this.get(model.id) !== model) {
        model.unsubscribe();
      }
    });
  }

  @action
  retainModel(model) {
    const fetched = this.get(model.id);

    if (fetched === model) {
      return;
    }

    if (fetched) {
      // pick up list-level fields (title, pinned, updated_at, user) without clobbering client-side chat state
      model.set(omit(fetched.toJS(), CLIENT_STATE_FIELDS));
      this.original = this.original.map((m) => (m === fetched ? model : m));
    } else {
      // not saved yet (or not visible in the list) -- keep it so the open chat isn't lost
      this.original = [model, ...this.original];
    }

    model.collection = this;
    this.filter();
    this.hashModelsById();
  }

  searchChats(query) {
    if (!query || typeof query !== 'string' || query.trim().length === 0) {
      return this.clearFilters();
    }

    return this.filter((chat) => {
      const title = chat.get('session_title') || chat.get('user_title') || '';
      return title.toLowerCase().includes(query.toLowerCase());
    });
  }

  @computed
  get userChats() {
    const userId = $auth.getActiveUserProperty('id');
    return this.models
      .filter((chat) => chat.get('user_id') === userId)
      .sort((a, b) => {
        const aPinned = a.get('pinned') ? 1 : 0;
        const bPinned = b.get('pinned') ? 1 : 0;
        return bPinned - aPinned || new Date(b.get('updated_at')) - new Date(a.get('updated_at'));
      });
  }

  @computed
  get companyChats() {
    const userId = $auth.getActiveUserProperty('id');
    return this.models
      .filter((chat) => $auth.isSudoer || !chat.isKProxyUserChat)
      .filter((chat) => chat.get('user_id') !== userId)
      .sort((a, b) => new Date(b.get('updated_at')) - new Date(a.get('updated_at')));
  }
}

export default ChatSessionCollection;
